import { ScoreCriteriaType } from "../../../schema/score.schema";

export interface SegmentWarnings {
	/** Cells (company/investor + persona) that have more than one ACTIVE lead scoring connected. */
	multipleActiveCells: Set<string>;
	/** Cells that have exactly one lead scoring connected and it is inactive. */
	inactiveOnlyCells: Set<string>;
}

/**
 * Classify each applicable segment cell by the lead scorings connected to it.
 *
 * A cell is keyed by `${companyId}-${personaId}` for company/partner segments and by
 * `investor|${investorName}|${personaId}` for investor segments. Only ACTIVE (`locked`) lead
 * scorings count toward the "more than one" warning — inactive scorings are ignored for that
 * check, since they are not applied. A cell is flagged when more than one ACTIVE scoring is
 * connected to it (a genuine ambiguity), regardless of how many inactive ones it also has.
 *
 * "More than one" counts DISTINCT scorings, not connection rows. A cell can hold the same
 * scoring twice — the table shows one chip either way, so counting rows produced a banner
 * naming an ambiguity that does not exist and that the user cannot find or clear
 * (PREC-3946).
 */
export const computeSegmentWarnings = (
	allCriteria: ScoreCriteriaType[],
	isApplicable: (companyId: number, personaId: number) => boolean,
	isApplicableForInvestor: (personaId: number) => boolean
): SegmentWarnings => {
	const cellMap = new Map<string, ScoreCriteriaType[]>();

	for (const criteria of allCriteria) {
		for (const ccp of criteria.scoreCriteriaCompaniesPersonas) {
			const isInvestorEntry = !!ccp.investor;

			if (isInvestorEntry && !ccp.scorePersonaCategoryId) {
				continue;
			}

			if (
				!isInvestorEntry &&
				(ccp.scoreCompanyCategoryId == null || ccp.scorePersonaCategoryId == null)
			) {
				continue;
			}

			if (isInvestorEntry && !isApplicableForInvestor(ccp.scorePersonaCategoryId)) {
				continue;
			}

			if (
				!isInvestorEntry &&
				!isApplicable(ccp.scoreCompanyCategoryId!, ccp.scorePersonaCategoryId!)
			) {
				continue;
			}

			const key = isInvestorEntry
				? `investor|${ccp.investor}|${ccp.scorePersonaCategoryId}`
				: `${ccp.scoreCompanyCategoryId}-${ccp.scorePersonaCategoryId}`;

			if (!cellMap.has(key)) {
				cellMap.set(key, []);
			}

			cellMap.get(key)!.push(criteria);
		}
	}

	const multipleActiveCells = new Set<string>();
	const inactiveOnlyCells = new Set<string>();

	for (const [key, criteriaList] of cellMap.entries()) {
		const connected = new Set(criteriaList.map((c) => c.id));
		const active = new Set(criteriaList.filter((c) => c.locked).map((c) => c.id));

		if (active.size > 1) {
			multipleActiveCells.add(key);
		}

		if (connected.size === 1 && active.size === 0) {
			inactiveOnlyCells.add(key);
		}
	}

	return { multipleActiveCells, inactiveOnlyCells };
};
